/**
 * Apply an Edit > Undo / Redo menu command.
 *
 * Text fields use the browser history. The editor callback is the manuscript
 * (or notes) port. Callers always pass that callback; splash passes a no-op
 * because it has no editor.
 */

import { isNothing, just, nothing, toMaybe, type Maybe } from "./maybe";
import {
  RICH_EDITOR_SELECTOR,
  editHistorySurface,
  type EditFieldSnapshot,
  type EditHistoryKind,
} from "./editHistorySurface";

function booleanProp(element: Element, key: "disabled" | "readOnly"): boolean {
  const present = key in element;
  if (present === false) return false;
  const record = element as unknown as Readonly<Record<string, unknown>>;
  return record[key] === true;
}

function contentEditable(element: Element): boolean {
  return element instanceof HTMLElement && element.isContentEditable;
}

function snapshotElement(element: Element): EditFieldSnapshot {
  return {
    tag: element.tagName,
    disabled: booleanProp(element, "disabled"),
    readOnly: booleanProp(element, "readOnly"),
    contentEditable: contentEditable(element),
    insideRichEditor: element.closest(RICH_EDITOR_SELECTOR) !== null,
  };
}

function activeEditField(): Maybe<EditFieldSnapshot> {
  const active = toMaybe(document.activeElement);
  if (isNothing(active)) return nothing;
  return just(snapshotElement(active.value));
}

export function applyEditHistory(
  kind: EditHistoryKind,
  editor: () => void,
): void {
  const surface = editHistorySurface(activeEditField());
  if (surface === "native-field") {
    void document.execCommand(kind);
    return;
  }
  editor();
}
